import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'
import { AppDispatch, RootState } from 'store'
import { Pagination } from 'components/Pagination/Pagination'
import { Option, Select } from 'components/Select/Select'
import { OptionsProps, Table } from 'components/Table/Table'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Flex } from 'styling/GlobalStyles'
import { MediumText } from 'styling/TypographyV2'
import { useDebounce } from 'hooks/useDebounde'
import { NavBarSearchIcon } from 'assets/icons/NavBarSearchIcon'
import {
  getOperationalTeams,
  getSimpleOperationalTeams,
  getSupervisorCandidates,
} from 'features/operationalManagement/operationalManagementSlice'
import {
  IOperationalTeam,
  IOperationalTeamSupervisor,
  TEAM_TYPE_LABEL,
  TeamType,
  getTeamTypeAccent,
} from 'features/operationalManagement/operationalManagement.types'
import { FiltersRow, SearchField } from '../Organogram.styled'
import { ORGANOGRAM_PAGE_LIMIT as TEAMS_PAGE_LIMIT } from '../constants'

const typeOptions = Object.entries(TEAM_TYPE_LABEL).map(([value, label]) => ({
  label,
  value: Number(value),
}))

const renderSupervisors = (supervisors: IOperationalTeamSupervisor[]): string => {
  if (supervisors.length === 0) return 'Unassigned'
  const visible = supervisors.slice(0, 2).map((s) => `${s.user.firstName} ${s.user.lastName}`)
  return visible.join(', ')
}

interface TeamsTabProps {
  refreshKey?: number
}

export const TeamsTab = ({ refreshKey = 0 }: TeamsTabProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const theme = useTheme() as any
  const { teams, isLoadingTeams, simpleTeams, supervisorCandidates } = useSelector(
    (state: RootState) => state.operationalManagement,
  )

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 400)

  const [typeValue, setTypeValue] = useState<TeamType | null>(null)
  const [supervisorId, setSupervisorId] = useState<number | null>(null)
  const [parentTeamId, setParentTeamId] = useState<number | null>(null)

  useEffect(() => {
    dispatch(getSimpleOperationalTeams(undefined))
    dispatch(getSupervisorCandidates(undefined))
  }, [dispatch])

  const filtersRef = useRef({
    debouncedSearch,
    typeValue,
    supervisorId,
    parentTeamId,
  })
  const lastRefreshKeyRef = useRef(refreshKey)

  useEffect(() => {
    const prev = filtersRef.current
    const filtersChanged =
      prev.debouncedSearch !== debouncedSearch ||
      prev.typeValue !== typeValue ||
      prev.supervisorId !== supervisorId ||
      prev.parentTeamId !== parentTeamId
    const refreshed = lastRefreshKeyRef.current !== refreshKey
    filtersRef.current = { debouncedSearch, typeValue, supervisorId, parentTeamId }
    lastRefreshKeyRef.current = refreshKey

    if ((filtersChanged || refreshed) && page !== 1) {
      setPage(1)
      return
    }
    const q = debouncedSearch.trim()
    dispatch(
      getOperationalTeams({
        page,
        limit: TEAMS_PAGE_LIMIT,
        ...(q ? { q } : {}),
        ...(typeValue ? { types: String(typeValue) } : {}),
        ...(supervisorId ? { supervisorIds: String(supervisorId) } : {}),
        ...(parentTeamId ? { parentTeamIds: String(parentTeamId) } : {}),
      }),
    )
  }, [dispatch, page, debouncedSearch, typeValue, supervisorId, parentTeamId, refreshKey])

  const parentTeamOptions = useMemo<Option[]>(
    () => simpleTeams.map((t) => ({ label: t.name, value: t.id })),
    [simpleTeams],
  )
  const supervisorOptions = useMemo<Option[]>(
    () => supervisorCandidates.map((u) => ({ label: u.fullName, value: u.id })),
    [supervisorCandidates],
  )
  const typeOptionsTyped = useMemo<Option[]>(
    () => typeOptions.map((o) => ({ label: o.label, value: o.value })),
    [],
  )

  const findOption = (opts: Option[], v: number | null): Option | null =>
    v == null ? null : opts.find((o) => o.value === v) ?? null

  const nullType: Option = { label: 'All types', value: null }
  const nullSupervisor: Option = { label: 'All supervisors', value: null }
  const nullParent: Option = { label: 'All parent teams', value: null }

  const teamTableOptions = useMemo<OptionsProps<IOperationalTeam>[]>(
    () => [
      {
        header: 'TEAM',
        cell: (team) => <MediumText bold>{team.name}</MediumText>,
      },
      {
        header: 'SUPERVISORS',
        cell: (team) => {
          const overflow = team.supervisors.length - 2
          return (
            <Flex center gap='0.25rem' wrap>
              <span>{renderSupervisors(team.supervisors)}</span>
              {overflow > 0 && <StatusPill status='accent'>{`+${overflow}`}</StatusPill>}
            </Flex>
          )
        },
      },
      {
        header: 'TYPE',
        cell: (team) => (
          <StatusPill status={team.type === TeamType.Operations || team.type === TeamType.Support ? 'info' : team.type === TeamType.Logistics ? 'success' : team.type === TeamType.Security ? 'danger' : team.type === TeamType.Maintenance || team.type === TeamType.Admin ? 'accent' : 'accent'}>{TEAM_TYPE_LABEL[team.type]}</StatusPill>
        ),
      },
      {
        header: 'MEMBERS',
        cell: (team) => team.memberCount,
      },
      {
        header: 'PARENT TEAM',
        cell: (team) => team.parentTeam?.name ?? '/',
      },
    ],
    [theme],
  )

  return (
    <>
      <FiltersRow>
        <SearchField>
          <NavBarSearchIcon />
          <input
            type='text'
            aria-label='Search for teams'
            placeholder='Search for teams...'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchField>

        <Select
          placeholder='All types'
          options={typeOptionsTyped}
          nullValue={nullType}
          value={findOption(typeOptionsTyped, typeValue)}
          onChange={(opt) =>
            setTypeValue(opt.value == null ? null : (opt.value as TeamType))
          }
        />

        <Select
          placeholder='All supervisors'
          options={supervisorOptions}
          nullValue={nullSupervisor}
          value={findOption(supervisorOptions, supervisorId)}
          onChange={(opt) =>
            setSupervisorId(opt.value == null ? null : (opt.value as number))
          }
        />

        <Select
          placeholder='Parent team'
          options={parentTeamOptions}
          nullValue={nullParent}
          value={findOption(parentTeamOptions, parentTeamId)}
          onChange={(opt) =>
            setParentTeamId(opt.value == null ? null : (opt.value as number))
          }
        />
      </FiltersRow>

      <Table<IOperationalTeam>
        cellKey='id'
        items={teams.data}
        loading={isLoadingTeams}
        emptyListOptions={{ title: 'No teams yet.' }}
        onClick={(team) => navigate(`/organogram/teams/${team.id}`)}
        options={teamTableOptions}
      />

      {teams.totalPages > 1 && (
        <Flex justcenter mt='16'>
          <Pagination pageCount={teams.totalPages} setPage={setPage} forcePage={page - 1} />
        </Flex>
      )}
    </>
  )
}
